import type { RunPhase } from '@/api/models';

/**
 * The plain-language phase vocabulary for the Run Ledger.
 *
 * These six stages are what a client sees while a change is being made. They
 * are ordered, and the order is load-bearing: the projection in
 * `projection.ts` refuses to move backwards through this list, so a
 * late-arriving or out-of-order event can never make the ledger appear to lose
 * its place.
 *
 * The wording describes what is happening to *their product*, never to a repo,
 * a branch or a build. "Saving the work" is a commit; the client does not need
 * to know that, and telling them would be a white-label failure.
 */

export interface PhaseDef {
  phase: RunPhase;
  /** The ledger caption. Present participle, so it reads as ongoing. */
  label: string;
  /** Shown when this is the live stage - one line, slightly more specific. */
  hint: string;
}

/** The six progress stages, in order. `finished` and `failed` are terminal, not stages. */
export const PHASE_SEQUENCE: PhaseDef[] = [
  { phase: 'queued', label: 'Queued', hint: 'Waiting for a free slot.' },
  { phase: 'preparing', label: 'Getting set up', hint: 'Opening a fresh copy of your product.' },
  { phase: 'working', label: 'Making the changes', hint: 'This is the part that takes the longest.' },
  { phase: 'committing', label: 'Saving the work', hint: 'Writing the change down so it is not lost.' },
  { phase: 'deploying', label: 'Publishing your preview', hint: 'Putting the change on your preview site.' },
  { phase: 'verifying', label: 'Checking it works', hint: 'Making sure the site came back up.' },
];

const INDEX: Record<string, number> = Object.fromEntries(
  PHASE_SEQUENCE.map((p, i) => [p.phase, i]),
);

export const TERMINAL_PHASES: RunPhase[] = ['finished', 'failed'];

export function isTerminalPhase(phase: RunPhase): boolean {
  return phase === 'finished' || phase === 'failed';
}

/** Position in the sequence. Terminal phases sort past the end. */
export function phaseIndex(phase: RunPhase): number {
  if (isTerminalPhase(phase)) return PHASE_SEQUENCE.length;
  return INDEX[phase] ?? 0;
}

export function phaseDef(phase: RunPhase): PhaseDef {
  const found = PHASE_SEQUENCE[phaseIndex(phase)];
  if (found) return found;
  return { phase, label: 'Working on it', hint: 'This is in progress.' };
}

/** Label for a phase, preferring the server's own client-safe label when it sends one. */
export function phaseLabel(phase: RunPhase, serverLabel?: string | null): string {
  const trimmed = serverLabel?.trim();
  if (trimmed) return trimmed;
  return phaseDef(phase).label;
}
